import type { AbsenceCategory, ScheduleEntry, WorkPhaseSource } from '@myapp/contract'
import { deriveWorkPhase } from '@myapp/contract'
import { AxiosError } from 'axios'

/**
 * Whether a session may open on this entry — the client's copy of the API's
 * `assertStartAllowed` (`work-item-lifecycle.service.ts`), so the worker gets the dialog without a
 * failed round trip first. The server stays the authority: `startGateFromError` handles the race
 * where the flag arrived after the page loaded.
 *
 * - `free` — not flagged, or the session is already open (a flag set mid-session never stops it).
 * - `confirm` — the office is rescheduling it; the worker may go ahead after confirming.
 * - `blocked` — flagged by the worker's own Krankheit or Urlaub; only the office can resolve that.
 */
export type StartGate =
  | { kind: 'free' }
  | { kind: 'confirm'; cause: ScheduleEntry['reschedulingCause']; reason: AbsenceCategory | null }
  | { kind: 'blocked'; reason: AbsenceCategory }

/** Same set as the API's `BLOCKING_OWN_ABSENCES` (user decision 05.10.2026). */
const BLOCKING_OWN_ABSENCES: ReadonlySet<AbsenceCategory> = new Set<AbsenceCategory>([
  'SICK_LEAVE',
  'VACATION',
])

export function resolveStartGate(
  schedule:
    | (WorkPhaseSource &
        Pick<ScheduleEntry, 'needsRescheduling' | 'reschedulingCause' | 'reschedulingReason'>)
    | null
    | undefined,
): StartGate {
  if (!schedule?.needsRescheduling || deriveWorkPhase(schedule) !== null) return { kind: 'free' }

  const cause = schedule.reschedulingCause ?? null
  const reason = schedule.reschedulingReason ?? null
  if (cause === 'ABSENCE' && reason != null && BLOCKING_OWN_ABSENCES.has(reason)) {
    return { kind: 'blocked', reason }
  }
  return { kind: 'confirm', cause, reason }
}

/**
 * How a flagged item is named to the Handwerker — a status, not the office's to-do
 * („Braucht Umdisponierung“). Amber, the same accent the calendar's ghosted blocks use; red would
 * read as an error the worker has to fix.
 */
export const WORKER_RESCHEDULING_LABEL = 'Wird umgeplant'
export const WORKER_RESCHEDULING_COLOR = '#F59E0B'

type GateErrorBody = {
  error?: string
  meta?: { cause?: ScheduleEntry['reschedulingCause']; reason?: AbsenceCategory | null } | null
}

/** The gate the server answered with, or `null` for any other failure. */
export function startGateFromError(error: unknown): Exclude<StartGate, { kind: 'free' }> | null {
  if (!(error instanceof AxiosError)) return null
  const data = error.response?.data as GateErrorBody | undefined
  if (data?.error === 'ITEM_NEEDS_RESCHEDULING') {
    return { kind: 'confirm', cause: data.meta?.cause ?? null, reason: data.meta?.reason ?? null }
  }
  if (data?.error === 'ITEM_BLOCKED_BY_OWN_ABSENCE' && data.meta?.reason) {
    return { kind: 'blocked', reason: data.meta.reason }
  }
  return null
}
